import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { VerifyResult } from '../../lib/types';
import { ErrorNote, Field, PageHeader, StatusBadge } from '../../ui/components';

/**
 * DocVault's serial-based staff lookup (GET /api/v1/docs/serial/:serial/status)
 * is a service-to-service endpoint (requireService()) — an officer's own
 * bearer token can't call it. The only staff-reachable verification surface
 * is the same public QR-token verify endpoint any citizen or verifier org
 * can use (GET /api/v1/docs/verify/:qrToken, mounted with no auth at all).
 * This console is that surface, reused from a staff screen — hence "QR
 * token", not "serial", even though staff may be handed either on paper.
 */
function extractToken(input: string): string {
  const trimmed = input.trim();
  const match = trimmed.match(/\/verify\/([^/?#]+)/);
  return match ? match[1]! : trimmed;
}

export function VerifyConsole() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const token = extractToken(input);
      const r = await api.get<VerifyResult>(`/api/v1/docs/verify/${encodeURIComponent(token)}`);
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Verification console"
        subtitle="Paste a document's QR token (or its full verify URL) to check its live status — this is the same public verify surface anyone can use, from a staff console"
      />
      <div className="card max-w-xl p-4">
        <Field label="QR token / verify URL">
          <input
            className="field-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. http://127.0.0.1:4103/api/v1/docs/verify/Abc123…"
            onKeyDown={(e) => e.key === 'Enter' && check()}
          />
        </Field>
        <button className="btn-primary mt-3" onClick={check} disabled={busy || !input.trim()}>
          {busy ? 'Checking…' : 'Check status'}
        </button>

        {error ? (
          <div className="mt-4">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {result ? (
          <div className="mt-4 rounded-lg border border-slate-200 p-3">
            {result.valid ? (
              <div className="mb-2 flex items-center gap-2">
                <StatusBadge status="VALID" />
                <span className="text-sm font-semibold text-slate-700">{result.docTypeCode}</span>
              </div>
            ) : (
              <div className="mb-2 flex items-center gap-2">
                <StatusBadge status={result.status ?? 'REJECTED'} />
                <span className="text-sm text-slate-500">{result.serial ? result.docTypeCode : 'No document found for this token'}</span>
              </div>
            )}
            {result.serial ? <div className="text-xs text-slate-500">Serial {result.serial}</div> : null}
            {result.issuedAt ? <div className="text-xs text-slate-500">Issued {new Date(result.issuedAt).toLocaleDateString('en-GB')}</div> : null}
            {result.expiresAt ? <div className="text-xs text-slate-500">Expires {new Date(result.expiresAt).toLocaleDateString('en-GB')}</div> : null}
            {result.statusReason ? <div className="mt-1 text-xs text-rose-600">{result.statusReason}</div> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
